import { getStationCode } from "../../network/NetworkUtil";
import {
  convertEpochMillisToLocalTime,
  convertMillisecondsToHHMM,
} from "../../util/Util";
import { getAPIGatewayDataFromParent } from "../../handler/ParentDataHandler";
import { DRIVER_TYPES, HTTP_SUCCESS_STATUS } from "../../util/Constants";
import {
  RSWS_NODE_CONFIG_METRIC,
  ROUTE_STATUS_METRIC_PROCESS_NAME,
  RSWS_STATION_ROUTES_METRIC,
  RSWS_FETCH_ROUTES_METRIC,
} from "../../util/MetricUtils";
import {
  getRouteStatus,
  updateUnassignedRouteStatus,
  PARTIALLY_STAGED,
  STAGED,
} from "../utils/EntityStatusManager";
import {
  getRouteStagingLocation,
  transformStagingLocations,
} from "../../outboundAmzl/util/DataTransformUtil.js";
import {
  getPicklistProgress,
  calculateStageDuration,
  getPicklistStatus,
} from "../utils/Util";
import {
  getCluster,
  getDateInMMDDYYYYWithOffset,
} from "../../outboundAmzl/util/Util";
import {
  logOperationalMetric,
  publishErrorToCSM,
} from "../../overview/utils/utils";
import {
  RECEIVE_DATA,
  RECEIVE_FILTER_DATA,
  RECEIVE_REFRESH_INTERVAL,
  RECEIVE_STATION_CONFIG,
  UPDATE_ROUTE_DATA_CACHE,
} from "../utils/ReducerActionTypes";
import { FALLBACK_DASH, FALLBACK_ELLIPSIS } from "../utils/Constants";
import { SLASH } from "../../util/Constants";

/** Get Route List **/

/**
 * Fetches route information from the API and dispatches the data to reducers
 * @param {Object} args - Configuration object
 * @param {Function} args.dispatcher - Main data dispatcher
 * @param {Function} args.filterDispatcher - Filter data dispatcher
 * @param {Object} args.stationConfig - Station configuration
 * @param {Function} args.configDispatch - Configuration dispatcher
 */
export async function fetchRouteInformation(args) {
  const { dispatcher, filterDispatcher, stationConfig, configDispatch } = args;
  const stationCode = getStationCode();
  try {
    const response = await getStationRoutes();
    const lastUpdated = new Date().getTime();
    if (response) {
      const output = response.data.routes;
      dispatcher({
        type: UPDATE_ROUTE_DATA_CACHE,
        data: {
          record: {
            response: response.data,
            stationCode: stationCode,
          },
          lastUpdated: lastUpdated,
        },
      });
      dispatcher({
        type: RECEIVE_DATA,
        data: {
          ...getRouteData(output, stationConfig),
          lastUpdated: lastUpdated,
        },
      });
      filterDispatcher({
        type: RECEIVE_FILTER_DATA,
        data: getFilterData(output),
      });
      configDispatch({
        type: RECEIVE_REFRESH_INTERVAL,
        data: {
          refreshInterval: response.data.routeRefreshRate,
        },
      });
    } else {
      // If getStationRoutes returns null, it means there was an error
      publishErrorToCSM({
        processName: ROUTE_STATUS_METRIC_PROCESS_NAME,
        metricName: RSWS_FETCH_ROUTES_METRIC,
      });
    }
  } catch (error) {
    console.error("Exception in RouteNetworkAPI.getRouteInformation", error);
    publishErrorToCSM({
      processName: ROUTE_STATUS_METRIC_PROCESS_NAME,
      metricName: RSWS_FETCH_ROUTES_METRIC,
    });
  }
}

/**
 * Retrieves station routes from API with authentication handling
 * @returns {Promise<Object|null>} API response or null on error
 */
async function getStationRoutes() {
  const startTime = Date.now();
  try {
    let response;
    const requestPayload = getRouteRequestPayload();
    response = await getAPIGatewayDataFromParent(requestPayload);
    if (response && response.status === HTTP_SUCCESS_STATUS) {
      logOperationalMetric(
        startTime,
        ROUTE_STATUS_METRIC_PROCESS_NAME,
        RSWS_STATION_ROUTES_METRIC,
        true,
      );
      return response;
    } else {
      console.error("Exception in RouteNetworkAPI.getStationRoutes", {
        response,
      });
      logOperationalMetric(
        startTime,
        ROUTE_STATUS_METRIC_PROCESS_NAME,
        RSWS_STATION_ROUTES_METRIC,
        false,
      );
    }
  } catch (e) {
    console.error("Exception in RouteNetworkAPI.getStationRoutes", e);
    logOperationalMetric(
      startTime,
      ROUTE_STATUS_METRIC_PROCESS_NAME,
      RSWS_STATION_ROUTES_METRIC,
      false,
    );
  }
  return null;
}

/**
 * Creates request payload for API Gateway route requests
 * @returns {Object} Request payload object
 */
function getRouteRequestPayload() {
  const mainUri = `/apigw/route/station/${getStationCode()}`;

  const requestBody = {
    date: [getDateInMMDDYYYYWithOffset(0)],
  };
  const requestPayload = {
    resourcePath: mainUri,
    httpMethod: "get",
    processName: "routeStaging",
    requestParams: requestBody,
  };
  return requestPayload;
}

/**
 * Processes raw route data and converts to UI model
 * @param {Array} output - Raw route data from API
 * @param {Object} stationConfig - Station configuration
 * @returns {Object} Processed route data model
 */
export function getRouteData(output, stationConfig) {
  const model = {};
  output = updateUnassignedRouteStatus(output);
  model.routeList = convertRouteToUIModel(output, stationConfig);
  return model;
}

/**
 * Converts route data to UI format
 * @param {Array} routes - Array of route objects
 * @param {Object} stationConfig - Station configuration
 * @returns {Array} Array of UI-formatted route objects
 */
function convertRouteToUIModel(routes, stationConfig) {
  return routes.map((route) => {
    let routeStatus = getRouteStatus(route);
    route.stagingLocations = transformStagingLocations(route);

    // Add transformed status to each picklist
    if (route.picklists) {
      route.picklists.forEach((picklist) => {
        picklist.status = getPicklistStatus(picklist);
      });
    }

    return {
      routeId: route.routeId,
      routeCode: route.routeCode,
      stationCode: route.stationCode,
      driverType: route.type,
      routeSLA: convertMillisecondsToHHMM(route.criticalPullTime), // Stage by time at route Level
      criticalPullTime: route.criticalPullTime,
      routeStatus: routeStatus,
      hazmatWeight: route.hazmatWeight ? route.hazmatWeight : "",
      driver: route.driver ? route.driver.name : FALLBACK_ELLIPSIS,
      sidelined: route.sidelined,
      stagingLocations: route.stagingLocations, // Represents list of staging location objects
      stagingLocation: getRouteStagingLocation(route), // Represents display label for staging location, using it in passing to route id info
      carts: route.carts,
      stageTime: getRouteStageTime(route, route.routeStatus),
      routeProgress: getRouteProgress(route),
      startTime: getRouteStartTime(route) /* associate start time */,
      endTime: getRouteEndTime(route),
      stageDuration: calculateStageDuration(route) /* Min in Status */,
      stageError: route.pickingErrorMessage,
      routeSpeed: route.routeSpeed,
      picklists: route.picklists,
    };
  });
}

/**
 * Extracts start time from route's first associate
 * @param {Object} route - Route object
 * @returns {string} Formatted start time or fallback value
 */
function getRouteStartTime(route) {
  if (
    route.picklists &&
    route.picklists.length > 0 &&
    route.picklists[0].associates &&
    route.picklists[0].associates.length > 0 &&
    route.picklists[0].associates[0].startTime
  ) {
    return convertEpochMillisToLocalTime(
      route.picklists[0].associates[0].startTime,
    );
  }
  return FALLBACK_ELLIPSIS;
}

/**
 * Extracts end time from route's first associate
 * @param {Object} route - Route object
 * @returns {string} Formatted end time or fallback value
 */
function getRouteEndTime(route) {
  if (
    route.picklists &&
    route.picklists.length > 0 &&
    route.picklists[0].associates &&
    route.picklists[0].associates.length > 0 &&
    route.picklists[0].associates[0].endTime
  ) {
    return convertEpochMillisToLocalTime(
      route.picklists[0].associates[0].endTime,
    );
  }
  return FALLBACK_ELLIPSIS;
}

/**
 * Calculates total progress across all picklists
 * @param {Object} route - Route object with picklists
 * @returns {string} Progress in format "completed/total" or "-"
 */
function getRouteProgress(route) {
  if (!route.picklists || route.picklists.length === 0) return FALLBACK_DASH;
  let totalCompleted = 0;
  let totalCount = 0;
  route.picklists.forEach((picklist) => {
    const progress = getPicklistProgress(picklist);
    if (progress !== FALLBACK_DASH && progress.includes(SLASH)) {
      const [completed, total] = progress.split(SLASH).map(Number);
      totalCompleted += completed;
      totalCount += total;
    }
  });
  return totalCount > 0
    ? `${totalCompleted}${SLASH}${totalCount}`
    : FALLBACK_DASH;
}

/**
 * Gets the latest stage time from route carts
 * @param {Object} route - Route object
 * @param {string} routeStatus - Current status
 * @returns {string|null} Latest stage time or null
 */
function getRouteStageTime(route, routeStatus) {
  if (
    Array.isArray(route.carts) &&
    route.carts.length &&
    (routeStatus === STAGED || routeStatus === PARTIALLY_STAGED) &&
    !route.sidelined
  ) {
    const cartList = route.carts.filter((cart) => cart.stageTime);
    if (cartList.length) {
      const lastCart = cartList.reduce((previous, current) =>
        previous.stageTime > current.stageTime ? previous : current,
      );
      return lastCart.stageTime;
    }
  }
  return null;
}

/**
 * Extracts filter data from route list for UI filtering
 * @param {Array} output - Array of route objects
 * @returns {Object} Filter data with departure, driverType, and cluster sets
 */
export function getFilterData(output) {
  const data = {
    departure: new Set(),
    driverType: new Set([DRIVER_TYPES.DSP, DRIVER_TYPES.DP]),
    cluster: new Set(),
  };
  output.forEach((route) => {
    if (route.type !== FALLBACK_ELLIPSIS) {
      data.driverType.add(route.type);
    }
    data.cluster.add(getCluster(route.routeCode));
    if (route.criticalPullTime != null) {
      data.departure.add(convertMillisecondsToHHMM(route.criticalPullTime));
    }
  });
  return data;
}

/** Get Station Config **/
/**
 * Fetches node configuration from API
 * @param {Function} dispatcher - Redux dispatcher
 * @returns {Promise<null>} Always returns null
 */
export async function fetchNodeConfig(dispatcher) {
  try {
    await getNodeConfig(dispatcher);
    return null;
  } catch (error) {
    console.error("Exception in RouteNetworkAPI.fetchNodeConfig", error);
    publishErrorToCSM({
      processName: ROUTE_STATUS_METRIC_PROCESS_NAME,
      metricName: RSWS_NODE_CONFIG_METRIC,
    });
    return null;
  }
}

/**
 * Retrieves node configuration via API Gateway
 * @param {Function} dispatcher - Redux dispatcher
 * @returns {Promise<null>} Always returns null
 */
async function getNodeConfig(dispatcher) {
  const startTime = Date.now();
  const stationCode = getStationCode();
  try {
    let response;
    const requestPayload = {
      resourcePath: "/apigw/config",
      requestParams: { stationCode: [stationCode] },
      httpMethod: "get",
      processName: "routeStaging",
    };
    response = await getAPIGatewayDataFromParent(requestPayload);
    if (response && response.status === HTTP_SUCCESS_STATUS) {
      logOperationalMetric(
        startTime,
        ROUTE_STATUS_METRIC_PROCESS_NAME,
        RSWS_NODE_CONFIG_METRIC,
        true,
      );
      const stationConfig = response.data;
      dispatcher({
        type: RECEIVE_STATION_CONFIG,
        data: {
          stationConfig: {
            initialised: true,
            ...stationConfig,
            refreshInterval: stationConfig.routeRefreshRate,
          },
        },
      });
    } else {
      console.error("Exception in RouteNetworkAPI.getNodeConfig", { response });
      logOperationalMetric(
        startTime,
        ROUTE_STATUS_METRIC_PROCESS_NAME,
        RSWS_NODE_CONFIG_METRIC,
        false,
      );
    }
  } catch (e) {
    console.error("Exception in RouteNetworkAPI.getNodeConfig", e);
    logOperationalMetric(
      startTime,
      ROUTE_STATUS_METRIC_PROCESS_NAME,
      RSWS_NODE_CONFIG_METRIC,
      false,
    );
    publishErrorToCSM({
      processName: ROUTE_STATUS_METRIC_PROCESS_NAME,
      metricName: RSWS_NODE_CONFIG_METRIC,
    });
  }
  return null;
}



// WEBPACK FOOTER //
// ./src/components/RouteStatus/network/RouteNetworkAPI.js